import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IFormulaResponse, IFormulaUsage } from './formula.model';

@Injectable({
	providedIn: 'root',
})
export class FormulaRequester extends DcBaseRequester<IFormulaUsage> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('FORMULAS_USAGES');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getFormulasUsages(): Observable<ReadonlyArray<IFormulaUsage>> {
		const url = this.urlService.generateUrl(
			this.elementsListUrl,
			{},
			{
				project: this.projectId,
			}
		);
		return this.get<ReadonlyArray<IFormulaUsage>>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.NetworkAndCache,
				evictTimeoutInSeconds: 5 * 60,
				isCacheable: true,
			})
		);
	}

	public getFormulaList(
		useCache: boolean
	): Observable<ReadonlyArray<IFormulaResponse>> {
		const resourceUrl = this.generateResourceUrl('FORMULA_CATALOG');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				shared: true,
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IFormulaResponse>>(url, cachePolicy);
	}

	public deleteFormula(formulaId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('FORMULA_CATALOG_ONE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				itemId: formulaId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}
	public deleteMany(itemsIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('FORMULA_CATALOG');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				itemsIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateFormula(formulaId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			'FORMULA_CATALOG_ONE_DUPLICATE'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				itemId: formulaId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<void>(url, {});
	}

	public updateSharedStatus(
		formulaId: number,
		shared: boolean
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl('FORMULA_CATALOG_ONE_STATUS');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				itemId: formulaId,
			},
			{
				project: this.projectId,
				shared,
			}
		);
		return this.http.put<void>(url, {});
	}
}
